import { createSelector } from "@reduxjs/toolkit";

import { createDeepEqualSelector } from "../utils/createDeepEqualSelector";
import { selectAllAccounts } from "../accounts";

import { formatBulkPayment } from "./helpers";

import type { RootState } from "../store";

// Basic selectors
export const selectBulkPaymentsState = (state: RootState) => state.bulkPayments;

export const selectBulkPaymentsById = createSelector(
  [selectBulkPaymentsState],
  (bulkPaymentsState) => bulkPaymentsState.byId,
);

export const selectBulkPaymentsList = createSelector(
  [selectBulkPaymentsState],
  (bulkPaymentsState) => bulkPaymentsState.list,
);

export const selectBulkPaymentsTotal = createSelector(
  [selectBulkPaymentsState],
  (bulkPaymentsState) => bulkPaymentsState.total,
);

export const selectSelectedBulkPayment = createSelector(
  [selectBulkPaymentsState],
  (bulkPaymentsState) => bulkPaymentsState.selected,
);

export const selectBulkPaymentsFilters = createSelector(
  [selectBulkPaymentsState],
  (bulkPaymentsState) => bulkPaymentsState.filters,
);

export const selectBulkPaymentsPageMap = createSelector(
  [selectBulkPaymentsState],
  (bulkPaymentsState) => bulkPaymentsState.pageMap,
);

// Computed selectors
export const selectAllBulkPayments = createDeepEqualSelector(
  [selectBulkPaymentsById, selectBulkPaymentsList],
  (byId, list) => (list ?? []).map((id) => byId[id]).filter(Boolean),
);

export const selectBulkPaymentById = (uuid: string) =>
  createSelector([selectBulkPaymentsById], (byId) => byId[uuid]);

export const selectFormattedBulkPayments = createDeepEqualSelector(
  [selectBulkPaymentsList, selectBulkPaymentsById, selectAllAccounts],
  (list, byId, accounts) => {
    // list can rehydrate as undefined from persisted state that predates this field
    return (list ?? [])
      .map((id) => {
        const bulkPayment = byId[id];
        if (!bulkPayment) return null;
        return formatBulkPayment(bulkPayment, accounts);
      })
      .filter(Boolean);
  },
);

export const selectFormattedPaginatedBulkPayments = createDeepEqualSelector(
  [
    selectBulkPaymentsPageMap,
    selectBulkPaymentsById,
    selectAllAccounts,
    (_, pageNumber: number) => pageNumber,
  ],
  (pageMap, byId, accounts, pageNumber) => {
    const ids = pageMap[pageNumber] ?? [];

    return ids
      ?.map((id) => {
        const activity = byId[id];
        if (!activity) return null;
        return formatBulkPayment(activity, accounts);
      })
      ?.filter(Boolean);
  },
);
